import {EventEmitter, Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import {UserData} from '../interfaces/users';
import {StorageService} from './storage.service';
import {Subscription} from 'rxjs';
import {Ride, RideStatus, Shop} from '../interfaces/parkenha';
import {ModalController} from '@ionic/angular';
import {ReviewRidePage} from '../modals/review-ride/review-ride.page';
import {PaymentMethod, ViewDataExtra} from '../interfaces/shared';

@Injectable({
  providedIn: 'root'
})
export class BrainService {
  public userId: string;
  public hasRide = false;
  public currentRide: Ride | null = null;
  public shopsData: {venueId: string; shops: Shop[]} | null = null;
  public invoiceUrl: string | null = null;
  public disableReview = false;
  public lastCompletedRide: {id: string; venue: ViewDataExtra; arrivedDate: number; overridden?: boolean} | null = null;
  private rideDataSubscription: Subscription;
  private invoiceSubscription: Subscription;
  private overrideHandledRideId: string | null = null;

  constructor(
    private database: AngularFirestore,
    private storage: StorageService,
    private modalCtrl: ModalController
  ) { }

  initializeUser(userId: string) {
    return new Promise((resolve, reject) => {
      if (this.userId !== userId) {
        this.lastCompletedRide = null;
      }

      this.userId = userId;
      this.database.collection('users').doc(userId).get().toPromise().then((doc) => {
        if (doc.exists) {
          const userData = doc.data() as UserData;
          userData.id = doc.id;
          this.storage.setObject('userData', userData);
          this.initializeSubscription();
          resolve(userData);
        } else {
          this.initializeSubscription();
          resolve(null);
        }
      }).catch((error) => {
        reject(error);
      });
    });
  }

  initializeSubscription() {
    if (this.userId) {
      if (this.rideDataSubscription) {
        this.rideDataSubscription.unsubscribe();
      }

      // eslint-disable-next-line max-len
      this.rideDataSubscription = this.database.collection('rides', ref => ref.where('customerId', '==', this.userId).where('status', '!=', RideStatus.carReturned)).valueChanges({idField: 'id'}).subscribe((data) => {
        this.hasRide = data.length > 0;
        if (this.hasRide) {
          this.lastCompletedRide = null;
          this.currentRide = data[data.length - 1] as Ride;
          if (this.currentRide.paymentMethod === PaymentMethod.card) {
            this.subscribeToInvoice();
          }
        } else {
          if (this.currentRide) {
            const completedRideId = this.currentRide.id;
            this.lastCompletedRide = {
              id: completedRideId,
              venue: this.currentRide.venue,
              arrivedDate: this.currentRide.completedDate || this.currentRide.changeDate || Date.now()
            };
            this.checkIfRideWasOverridden(completedRideId);
          }
          this.currentRide = null;
          if (this.invoiceSubscription) {
            this.invoiceSubscription.unsubscribe();
          }
        }
        this.checkIfShopsAreLoaded();
      });
    }
  }

  subscribeToInvoice() {
    // eslint-disable-next-line max-len
    this.invoiceSubscription = this.database.collection('invoices').doc(this.currentRide.id).valueChanges({idField: 'id'}).subscribe((data: any) => {
      console.log(data);
      if (data && data.stripeInvoiceUrl) {
        this.invoiceUrl = data.stripeInvoiceUrl;
      }
    });
  }

  async checkIfShopsAreLoaded() {
    if (this.currentRide) {
      if (!this.shopsData || this.shopsData.venueId !== this.currentRide.venueId) {
        const shopsDocs = await this.database.collection('vendors').doc(this.currentRide.venueId).collection('shops').get().toPromise();
        const shops = shopsDocs.docs.map((doc) => ({
            id: doc.id,
            ...doc.data() as Shop,
          }));
        this.shopsData = {
          venueId: this.currentRide.venueId,
          shops,
        };
      }
    } else {
      this.shopsData = null;
    }
  }

  async openReview(rideId: string) {
    const modal = await this.modalCtrl.create({
      component: ReviewRidePage,
      componentProps: {
        rideId
      },
      cssClass: 'review-modal-width'
    });
    await modal.present();
  }

  // When a ride disappears from the active query it may have been completed
  // normally OR overridden by the vendor. The live query filters out
  // `carReturned` rides, so we fetch the final document to detect an override
  // (marked by carOverrideTime/overrideReason set by the overrideRideComplete
  // cloud function). If overridden, skip the "car waiting" page and open the
  // rate-experience popup immediately.
  async checkIfRideWasOverridden(rideId: string) {
    if (this.overrideHandledRideId === rideId) {
      return;
    }
    try {
      const snap = await this.database.collection('rides').doc(rideId).get().toPromise();
      const finalRide = snap.data() as Ride | undefined;
      const overridden = !!(finalRide && (finalRide.carOverrideTime || finalRide.overrideReason));
      if (overridden && this.lastCompletedRide?.id === rideId) {
        this.overrideHandledRideId = rideId;
        this.lastCompletedRide.overridden = true;
        this.openOverrideReview(rideId);
      }
    } catch (error) {
      console.error('Failed to check ride override state', error);
    }
  }

  async openOverrideReview(rideId: string) {
    const modal = await this.modalCtrl.create({
      component: ReviewRidePage,
      componentProps: {
        rideId
      },
      cssClass: 'review-modal-width'
    });
    await modal.present();
    // Whether the customer submits or closes the popup, an overridden ride
    // returns to the scan/home screen (no "car waiting" page).
    await modal.onDidDismiss();
    if (this.lastCompletedRide?.id === rideId) {
      this.lastCompletedRide = null;
    }
  }
}
